import type { OwnerId } from 'prove-common'
import { HIDDEN_DELEGATION_UNITS } from '../config/hiddenDelegationUnits'
import { isDelegationPathHidden } from './delegationVisibility'
import {
  distributeEvenly,
  findByOwnerPath,
  setQuantityAtPath,
  sumPlannedQuantity,
  type DelegationNode
} from './taskDelegationUtils'

/** Recalculate totals and prune empty nodes using the same rule at every depth. */
const rebuildClearedNode = (
  node: DelegationNode,
  children: DelegationNode[]
): DelegationNode[] => {
  const retained = node.retainedQuantity ?? 0
  if (children.length === 0 && retained === 0 && (node.samplesTaken ?? 0) === 0)
    return []
  return [
    {
      ...node,
      children,
      plannedQuantity: retained + sumPlannedQuantity(children)
    }
  ]
}

/** Clear visible, editable subtrees, preserving hidden/read-only branches verbatim. */
export const clearVisibleDelegations = (
  nodes: DelegationNode[],
  parentPath: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS,
  canClear: (ownerId: OwnerId) => boolean = () => true
): DelegationNode[] =>
  nodes.flatMap((node) => {
    const path = [...parentPath, node.ownerId]
    if (isDelegationPathHidden(path, hiddenUnits) || !canClear(node.ownerId))
      return [node]
    return rebuildClearedNode(
      { ...node, retainedQuantity: 0 },
      clearVisibleDelegations(node.children, path, hiddenUnits, canClear)
    )
  })

/** Clear the selected scope, preserving retained amounts above it and pruning empty ancestors. */
export const clearVisibleDelegationsAtPath = (
  nodes: DelegationNode[],
  path: OwnerId[],
  hiddenUnits: ReadonlySet<string> = HIDDEN_DELEGATION_UNITS,
  canClear: (ownerId: OwnerId) => boolean = () => true
): DelegationNode[] => {
  const walk = (current: DelegationNode[], depth: number): DelegationNode[] => {
    if (depth === path.length)
      return clearVisibleDelegations(current, path, hiddenUnits, canClear)

    return current.flatMap((node) => {
      if (node.ownerId.toString() !== path[depth].toString()) return [node]
      const atScope = depth === path.length - 1
      // The selected root may be read-only while some of its children are editable.
      const updated = atScope
        ? {
            ...node,
            retainedQuantity: canClear(node.ownerId)
              ? 0
              : (node.retainedQuantity ?? 0)
          }
        : node
      return rebuildClearedNode(updated, walk(node.children, depth + 1))
    })
  }
  return walk(nodes, 0)
}

/** Targets are resolved by each view; both use the same stable rounding order and updates. */
export const fillDelegationRemainder = (
  nodes: DelegationNode[],
  targetPaths: OwnerId[][],
  remainder: number
): DelegationNode[] => {
  const paths = [
    ...new Map(
      targetPaths.map((path) => [JSON.stringify(path), path])
    ).entries()
  ]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, path]) => path)
  const shares = distributeEvenly(remainder, paths.length)
  return paths.reduce((next, path, index) => {
    if (shares[index] <= 0) return next
    const quantity = findByOwnerPath(next, path)?.plannedQuantity ?? 0
    return setQuantityAtPath(next, path, quantity + shares[index])
  }, nodes)
}
